<script setup>
import CommitteeService from '@/services/quality/committee.service'
import MeetingHeader from '@/views/quality/committee/MeetingHeader.vue'
import MeetingAdjournDialog from '@/views/quality/committee/MeetingAdjournDialog.vue'
import MeetingAttendanceSection from '@/views/quality/committee/sections/MeetingAttendanceSection.vue'
import MeetingAgendaSection from '@/views/quality/committee/sections/MeetingAgendaSection.vue'
import MeetingMinutesSection from '@/views/quality/committee/sections/MeetingMinutesSection.vue'
import MeetingActionsSection from '@/views/quality/committee/sections/MeetingActionsSection.vue'

// VSkeletonLoader is a Vuetify labs component and is NOT auto-registered —
// see src/pages/app/quality/committee/[id].vue, which uses it with no import
// and renders blank skeletons as a result (Task 7 fixes that page; this one
// must not repeat the bug).
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

// Same subject/action as the meetings list (Task 3) — both surfaces guard the
// same viewAny/view permission, quality.committee.menu.meetings.
definePage({
  meta: {
    subject: 'committee.menu',
    action: 'meetings',
  },
})

const route = useRoute()
const router = useRouter()

const meeting = ref(null)
const isLoading = ref(false)

// Monotonic token guarding against out-of-order responses — same pattern as
// src/pages/app/quality/committee/[id].vue.
let fetchToken = 0

const fetchMeeting = async () => {
  const token = ++fetchToken

  isLoading.value = true
  await CommitteeService.showMeeting(route.params.id, data => {
    if (token !== fetchToken) return
    meeting.value = data?.data ?? data ?? null
    isLoading.value = false
  }, () => {
    if (token !== fetchToken) return
    isLoading.value = false
  })
}

fetchMeeting()

watch(() => route.params.id, () => fetchMeeting())

const activeTab = ref('agenda')

// quorum_rule_snapshot is written only by QuorumService::persist(), reached
// from hold()/adjourn(). Its presence is the only reliable "an evaluation has
// happened" signal — quorum_met is null both before evaluation and when the
// evaluation reached no verdict.
const isQuorumEvaluated = computed(() => !!meeting.value?.quorum_rule_snapshot)

// ── Lifecycle actions ────────────────────────────────
// Start and Hold are plain no-body POSTs with no confirmation step, mirroring
// this module's own convention for reversible/idempotent transitions (see
// RcaService.verificationRequest's usage in the RCA sidebar). Adjourn and
// Cancel carry consequences or need a reason, so each gets its own dialog
// that owns the service call.
const actionLoading = ref(null)

const handleStart = async () => {
  actionLoading.value = 'start'
  await CommitteeService.startMeeting(route.params.id, () => {
    actionLoading.value = null
    fetchMeeting()
  }, () => {
    actionLoading.value = null
  })
}

const handleHold = async () => {
  actionLoading.value = 'hold'
  await CommitteeService.holdMeeting(route.params.id, () => {
    actionLoading.value = null
    fetchMeeting()
  }, () => {
    actionLoading.value = null
  })
}

const isAdjournDialogVisible = ref(false)

// ── Cancel dialog ────────────────────────────────────
// Owned by the page rather than a fourth dedicated component — the brief
// lists MeetingAdjournDialog as the one dialog file this task adds; Cancel's
// reason form mirrors CommitteeDissolveDialog's own shape inline here, the
// same way the outbreak workspace owns its own Close dialog rather than
// splitting it out of OutbreakHeader.
const isCancelDialogVisible = ref(false)
const refCancelForm = ref()
const isCancelling = ref(false)
const cancelForm = ref({ cancellation_reason: null })

const openCancelDialog = () => {
  cancelForm.value = { cancellation_reason: null }
  isCancelDialogVisible.value = true
}

const closeCancelDialog = () => {
  isCancelDialogVisible.value = false
  nextTick(() => refCancelForm.value?.resetValidation())
}

const submitCancel = async () => {
  const { valid } = await refCancelForm.value.validate()

  if (!valid) return

  isCancelling.value = true

  await CommitteeService.cancelMeeting(route.params.id, {
    cancellation_reason: cancelForm.value.cancellation_reason,
  }, () => {
    isCancelling.value = false
    closeCancelDialog()
    fetchMeeting()
  }, () => {
    isCancelling.value = false
  })
}
</script>

<template>
  <section v-if="meeting">
    <MeetingHeader
      :meeting="meeting"
      :action-loading="actionLoading"
      @start="handleStart"
      @hold="handleHold"
      @adjourn="isAdjournDialogVisible = true"
      @cancel="openCancelDialog"
    />

    <VCard
      variant="flat"
      class="border"
    >
      <VTabs v-model="activeTab">
        <VTab value="agenda">
          Agenda
        </VTab>
        <VTab value="attendance">
          Attendance
        </VTab>
        <VTab value="minutes">
          Minutes
        </VTab>
        <VTab value="actions">
          Actions
        </VTab>
      </VTabs>

      <VWindow v-model="activeTab">
        <!-- Agenda -->
        <VWindowItem value="agenda">
          <VCardText>
            <MeetingAgendaSection
              :meeting-id="route.params.id"
              :committee-id="meeting.committee_id"
              :agenda-items="meeting.agenda_items ?? []"
              :minutes-status="meeting.minutes_status"
              :meeting-status="meeting.status"
              @saved="fetchMeeting"
            />
          </VCardText>
        </VWindowItem>

        <!-- Attendance -->
        <VWindowItem value="attendance">
          <VCardText>
            <MeetingAttendanceSection
              :meeting-id="route.params.id"
              :attendees="meeting.attendees ?? []"
              :minutes-status="meeting.minutes_status"
              :meeting-status="meeting.status"
              :quorum-evaluated="isQuorumEvaluated"
              @saved="fetchMeeting"
            />
          </VCardText>
        </VWindowItem>

        <!-- Minutes -->
        <VWindowItem value="minutes">
          <VCardText>
            <MeetingMinutesSection
              :meeting-id="route.params.id"
              :committee-id="meeting.committee_id"
              :meeting-code="meeting.code"
              :meeting-status="meeting.status"
              :minutes-status="meeting.minutes_status"
              :minutes-body="meeting.minutes_body"
              :held-at="meeting.held_at"
              :scheduled-at="meeting.scheduled_at"
              :confirmed-in-meeting="meeting.confirmed_in_meeting"
              :agenda-items="meeting.agenda_items ?? []"
              @saved="fetchMeeting"
            />
          </VCardText>
        </VWindowItem>

        <!-- Actions -->
        <VWindowItem value="actions">
          <VCardText>
            <MeetingActionsSection
              :meeting-id="route.params.id"
              :committee-id="meeting.committee_id"
              :minutes-status="meeting.minutes_status"
              :meeting-status="meeting.status"
              :agenda-items="meeting.agenda_items ?? []"
              @saved="fetchMeeting"
            />
          </VCardText>
        </VWindowItem>
      </VWindow>
    </VCard>

    <MeetingAdjournDialog
      v-model:is-dialog-visible="isAdjournDialogVisible"
      :meeting-id="route.params.id"
      @adjourned="fetchMeeting"
    />

    <!-- Cancel Meeting Dialog -->
    <VDialog
      v-model="isCancelDialogVisible"
      max-width="500"
      persistent
      no-click-animation
    >
      <VCard>
        <VCardItem class="py-3 px-4">
          <template #title>
            <div class="d-flex align-center gap-2">
              <IconBtn
                variant="tonal"
                color="error"
                size="small"
              >
                <VIcon
                  icon="tabler-ban"
                  size="22"
                  color="error"
                />
              </IconBtn>
              <span class="text-h6 font-weight-medium">Cancel Meeting</span>
            </div>
          </template>
          <template #append>
            <IconBtn @click="closeCancelDialog">
              <VIcon icon="tabler-x" />
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <VCardText class="pa-4">
          <p class="text-body-2 text-medium-emphasis mb-4">
            This meeting will not take place. Provide a reason before cancelling.
          </p>
          <VForm ref="refCancelForm">
            <AppTextarea
              v-model="cancelForm.cancellation_reason"
              label="Cancellation Reason"
              placeholder="Enter cancellation reason"
              :rules="[requiredValidator]"
              :required="true"
              :is-speech-to-text="false"
            />
          </VForm>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-4 gap-2">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            @click="closeCancelDialog"
          >
            Back
          </VBtn>
          <VBtn
            color="error"
            variant="flat"
            :loading="isCancelling"
            @click="submitCancel"
          >
            Cancel Meeting
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>

  <!-- Loading state -->
  <section v-else-if="isLoading">
    <div class="d-flex align-center gap-4 mb-6">
      <VSkeletonLoader
        type="avatar"
        width="56"
      />
      <div>
        <VSkeletonLoader
          type="text"
          width="250"
        />
        <VSkeletonLoader
          type="text"
          width="180"
        />
      </div>
    </div>
    <VSkeletonLoader type="card" />
  </section>

  <!-- Error state — a 403/404 leaves meeting null with no message. -->
  <section v-else>
    <div class="text-center py-16">
      <VIcon
        icon="tabler-alert-triangle"
        size="48"
        class="text-disabled mb-2"
      />
      <p class="text-disabled mb-4">
        This meeting could not be found, or you do not have permission to view it.
      </p>
      <VBtn
        variant="tonal"
        prepend-icon="tabler-arrow-left"
        @click="router.push({ name: 'app-quality-committee-meetings' })"
      >
        Back to Meetings
      </VBtn>
    </div>
  </section>
</template>
